// DRAFT -- como e feito hoje na vida real.
//
// NBA: os 14 times que NAO foram aos playoffs entram na loteria. As tres
// piores campanhas tem 14% cada de ficar com a primeira escolha; dai as
// chances caem ate 0,5%. Sorteiam-se as QUATRO primeiras escolhas na loteria;
// da quinta em diante e ordem inversa da classificacao. A segunda rodada e
// ordem inversa pura, sem loteria. Sao 2 rodadas.
//
// NBB: draft de novos atletas, ordem inversa da classificacao, sem loteria.
//
// O que o olheiro ve NAO e o que o atleta e. Cada prospecto tem um overall e
// um potencial reais, e a tela mostra uma FAIXA -- quanto pior o departamento
// de olheiros, mais larga a faixa. Draft em que se enxerga o numero exato vira
// planilha, e a decisao deixa de ser decisao.

import { makeRng, clamp } from './rng.js';
import { makePlayer, POSITIONS } from './players.js';
import { enrichPlayer } from './economy.js';
import { sortedStandings } from './season.js';

/** Chances da loteria da NBA, em pontos percentuais, da pior campanha em diante. */
export const LOTTERY_ODDS = [14, 14, 14, 12.5, 10.5, 9, 7.5, 6, 4.5, 3, 2, 1.5, 1, 0.5];

/** Quantas escolhas do topo sao sorteadas na loteria (o resto e ordem inversa). */
export const LOTTERY_PICKS = 4;

/**
 * Classe de calouros. Sao gerados mais prospectos do que escolhas: quem sobra
 * fica sem clube, como na vida real.
 */
export function makeProspectClass({ seed, season, count, level, spread = 1 }) {
  const rng = makeRng(`draft|${seed}|${season}`);
  const takenNames = new Set();
  const prospects = [];

  for (let i = 0; i < count; i++) {
    // Curva da classe: dois ou tres nomes de elite, um monte de aposta.
    //
    // Calouro e CRU. Mesmo a primeira escolha do draft entra abaixo dos
    // titulares da liga -- o que ele tem e teto, nao presente. Sem esse
    // desconto o draft cospe novato de 90, melhor que quase todo o elenco, e
    // a temporada seguinte vira passeio.
    const tier = i / count;
    const talentNow = (1 - tier * 22) * spread;
    const player = makePlayer({
      position: POSITIONS[rng.int(POSITIONS.length)],
      talent: talentNow + (level - 75) - 9 + rng.normal() * 2.2,
      nationality: rng.chance(0.72) ? 'US' : 'BR',
      age: 19 + rng.int(3),
      seed: `prospecto|${seed}|${season}|${i}`,
      takenNames,
    });

    // Potencial do calouro: o teto e o produto real do draft.
    const room = clamp(24 - tier * 16 + rng.normal() * 4, 2, 30);
    player.potential = Math.round(clamp(player.overall + room, player.overall, 99));

    // O que o olheiro enxerga: uma faixa em volta da verdade.
    const fog = Math.round(clamp(3 + rng() * 7, 3, 11));
    player.scout = {
      floor: Math.max(40, player.potential - fog - rng.int(4)),
      ceiling: Math.min(99, player.potential + rng.int(fog)),
      // Nota do olheiro, 0..100: e por ela que a IA escolhe (e ela erra).
      grade: Math.round(clamp(
        (player.overall * 0.45 + player.potential * 0.55 - 45) * 2.6 + rng.normal() * 7,
        1, 99,
      )),
    };
    player.rookie = true;
    prospects.push(player);
  }

  // A ordem do board e a nota do olheiro, nao a verdade.
  prospects.sort((a, b) => b.scout.grade - a.scout.grade);
  return prospects;
}

/**
 * Roda a loteria e devolve a ordem da primeira rodada.
 * `nonPlayoff` sao os times fora dos playoffs, da PIOR campanha para a melhor.
 */
export function runLottery({ nonPlayoff, playoffOrder, rng }) {
  const pool = nonPlayoff.slice(0, LOTTERY_ODDS.length);
  const rest = nonPlayoff.slice(LOTTERY_ODDS.length);
  const odds = pool.map((_, i) => LOTTERY_ODDS[i] ?? 0.5);

  const winners = [];
  const available = pool.map((t, i) => ({ team: t, odds: odds[i] }));
  for (let p = 0; p < Math.min(LOTTERY_PICKS, available.length); p++) {
    const idx = rng.weighted(available.map((a) => a.odds));
    winners.push(available[idx].team);
    available.splice(idx, 1);
  }

  // Quem nao saiu na loteria segue a ordem inversa da classificacao.
  const remaining = pool.filter((t) => !winners.includes(t));
  return {
    order: [...winners, ...remaining, ...rest, ...playoffOrder],
    lotteryWinners: winners,
    lotteryPool: pool,
    odds,
  };
}

/**
 * Monta o draft da entressafra.
 * Devolve o objeto que vive em `state.draft`.
 */
export function buildDraft({ state, config, seed, rng }) {
  const standings = state.standings;
  const bracket = state.bracket;

  // Quem foi aos playoffs, na ordem em que caiu (o campeao escolhe por ultimo).
  const playoffTeamIds = new Set(
    bracket ? bracket.series.filter((s) => s.round === 1).flatMap((s) => s.teams) : [],
  );

  const all = sortedStandings(standings);
  const nonPlayoff = all
    .filter((r) => !playoffTeamIds.has(r.teamId))
    .map((r) => r.teamId)
    .reverse(); // pior campanha primeiro

  // Entre os classificados, quem foi mais longe escolhe depois.
  const depth = new Map();
  if (bracket) {
    for (const serie of bracket.series) {
      for (const teamId of serie.teams) {
        depth.set(teamId, Math.max(depth.get(teamId) || 0, serie.round));
      }
    }
    if (bracket.champion) depth.set(bracket.champion, 99);
  }
  const playoffOrder = all
    .filter((r) => playoffTeamIds.has(r.teamId))
    .map((r) => r.teamId)
    .sort((a, b) => {
      const da = depth.get(a) || 0;
      const db = depth.get(b) || 0;
      if (da !== db) return da - db;
      const ra = all.find((r) => r.teamId === a);
      const rb = all.find((r) => r.teamId === b);
      return (ra.wins / Math.max(1, ra.games)) - (rb.wins / Math.max(1, rb.games));
    });

  const draftConfig = config.draft || { rounds: 2, lottery: false };
  let order;
  let lottery = null;
  if (draftConfig.lottery && nonPlayoff.length >= 4) {
    const result = runLottery({ nonPlayoff, playoffOrder, rng });
    order = result.order;
    lottery = {
      pool: result.lotteryPool,
      odds: result.odds,
      winners: result.lotteryWinners,
    };
  } else {
    order = [...nonPlayoff, ...playoffOrder];
  }

  const totalPicks = order.length * draftConfig.rounds;
  const prospects = makeProspectClass({
    seed,
    season: state.season,
    count: Math.round(totalPicks * 1.25),
    level: config.level,
    spread: config.talentSpread ?? 1,
  });

  return {
    season: state.season + 1,
    rounds: draftConfig.rounds,
    order,
    lottery,
    prospects,
    picks: [],
    current: 0,
    done: false,
  };
}

/** De quem e a escolha da vez. */
export function currentPick(draft) {
  if (draft.done) return null;
  const round = Math.floor(draft.current / draft.order.length) + 1;
  const pick = (draft.current % draft.order.length) + 1;
  return { round, pick, teamId: draft.order[pick - 1], overall: draft.current + 1 };
}

/**
 * A escolha da IA. Ela usa a nota do OLHEIRO -- que erra -- e um pouco de
 * necessidade posicional: um time cheio de pivos pensa duas vezes antes de
 * levar mais um.
 */
export function aiChoice(draft, team, rng) {
  const available = draft.prospects.filter((p) => !p.draftedBy);
  if (!available.length) return null;

  const positionCount = {};
  for (const p of team.roster) positionCount[p.position] = (positionCount[p.position] || 0) + 1;

  const scored = available.slice(0, 12).map((p) => {
    const have = positionCount[p.position] || 0;
    const need = have >= 3 ? -6 : have === 0 ? 5 : 0;
    return { p, score: p.scout.grade + need + rng.normal() * 4 };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0].p;
}
